/**
 * Date formatting utilities for URL parameters
 */

/**
 * Format a Date object to MM-DD-YYYY string for URL
 * @param {Date|string|null} date - Date to format
 * @returns {string|null} - Formatted date string or null
 */
export const formatDateForUrl = (date) => {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  const year = d.getFullYear();
  return `${month}-${day}-${year}`;
};

/**
 * Parse MM-DD-YYYY string from URL to Date object
 * @param {string|null} dateStr - Date string in MM-DD-YYYY format
 * @returns {Date|null} - Parsed Date object or null
 */
export const parseDateFromUrl = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split("-");
  if (parts.length !== 3) return null;
  const [month, day, year] = parts.map(Number);
  if (isNaN(month) || isNaN(day) || isNaN(year)) return null;
  const date = new Date(year, month - 1, day);
  if (isNaN(date.getTime())) return null;
  return date;
};

/**
 * Convert an ISO date string (YYYY-MM-DD, as produced by <input type="date">) to MM/DD/YYYY.
 * Pure string op — no Date object, so it can't shift across timezones. Returns "" for empty
 * input and passes through values that are already MM/DD/YYYY.
 * @param {string|null} value
 * @returns {string}
 */
export const isoToMmddyyyy = (value) => {
  if (!value) return "";
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!iso) return value; // already MM/DD/YYYY (or unrecognized) — leave untouched
  const [, year, month, day] = iso;
  return `${month}/${day}/${year}`;
};

/**
 * Convert MM/DD/YYYY (or M/D/YYYY) to ISO YYYY-MM-DD for a native <input type="date">.
 * Pure string op. Returns "" for empty/unparseable input; passes through values already ISO.
 * @param {string|null} value
 * @returns {string}
 */
export const mmddyyyyToIso = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value; // already ISO
  const mdy = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!mdy) return "";
  const [, month, day, year] = mdy;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

/**
 * Parse an ISO date string (YYYY-MM-DD, datetime prefixes accepted) into a LOCAL-midnight Date.
 * Never `new Date(isoString)` — that parses as UTC midnight and shifts a day in negative-offset
 * timezones. Returns null for empty/unparseable input.
 * @param {string|null} value
 * @returns {Date|null}
 */
export const isoToLocalDate = (value) => {
  if (!value) return null;
  const iso = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!iso) return null;
  const [year, month, day] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  const date = new Date(year, month - 1, day);
  const overflowed = date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day;
  return overflowed ? null : date;
};

/**
 * Format a Date to ISO YYYY-MM-DD using its LOCAL components (counterpart of isoToLocalDate —
 * `toISOString()` would shift a day in positive-offset timezones). Returns "" for invalid input.
 * @param {Date|null} date
 * @returns {string}
 */
export const localDateToIso = (date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "";
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * Convert a datetime-local wall-clock string (YYYY-MM-DDTHH:mm), entered in the TENANT's
 * timezone, to a UTC ISO string. Uses native Intl (DST-correct, no extra deps). Falls back
 * to browser-local interpretation if no zone is given.
 * @param {string|null} localStr - "YYYY-MM-DDTHH:mm" wall-clock time meant for ianaZone
 * @param {string|null} ianaZone - IANA timezone (e.g. "America/New_York")
 * @returns {string|null} UTC ISO string, or null
 */
export const zonedLocalToUtcIso = (localStr, ianaZone) => {
  if (!localStr) return null;
  if (!ianaZone) return new Date(localStr).toISOString();
  const [datePart, timePart] = localStr.split("T");
  const [y, mo, day] = datePart.split("-").map(Number);
  const [h, mi] = (timePart || "00:00").split(":").map(Number);
  // Guess the instant as if the wall-clock were UTC, then correct by the zone's offset
  // at that instant (handles DST because the offset is computed for that specific moment).
  const utcGuess = Date.UTC(y, mo - 1, day, h, mi);
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: ianaZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = Object.fromEntries(dtf.formatToParts(new Date(utcGuess)).map((p) => [p.type, p.value]));
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second)
  );
  const offset = asUtc - utcGuess;
  return new Date(utcGuess - offset).toISOString();
};
